import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IProfileService } from '@intigral/domain/lib/application/services/ProfileService';
import userHelper from '../helpers/user';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@intigral/shared';
import { __ } from '../translations';
import { useState } from 'react';

export const useChannelFavorites = () => {
    const [isFavorite, setIsFavorite] = useState(false);

    const queryClient = useQueryClient();

    const favoriteChanels = useQuery<string[], Error>({
        queryKey: ['profiles', 'favorites'],
        queryFn: async () => {
            try {
                const selectedProfile = userHelper?.getProfile();
                const favoritesChannelsService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
                const response = await favoritesChannelsService.getFavoriteChannels(
                    userHelper.getAPIKey(),
                    selectedProfile?.profileGuid,
                    userHelper.getUserToken(),
                    userHelper.getIdentityToken(),
                    { device: 'PC', country: 'SA' }
                );
                return response ?? [];
            } catch (error) {
                throw new Error(error?.data?.message || 'Failed to fetch favorites');
            }
        },
    });

    const addFavorite = useMutation({
        mutationFn: async (channelId: string) => {
            setIsFavorite(true);
            const selectedProfile = userHelper?.getProfile();
            const favoritesChannelsService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
            const response = await favoritesChannelsService.addFavoriteChannels(
                userHelper.getAPIKey(),
                selectedProfile?.profileGuid,
                [channelId],
                userHelper.getUserToken(),
                userHelper.getIdentityToken()
            );
            return response;
        },
        onMutate: async (channelId: string) => {
            await queryClient.cancelQueries({ queryKey: ['profiles', 'favorites'] });
            const previousFavorites = queryClient.getQueryData<string[]>(['profiles', 'favorites']);
            // Optimistically update to include the new favorite
            queryClient.setQueryData<string[]>(['profiles', 'favorites'], (old = []) => [...old, channelId]);
            return { previousFavorites };
        },
        onError: (err: any, channelId, context) => {
            // Rollback if error
            setIsFavorite(false);
            queryClient.setQueryData(['profiles', 'favorites'], context?.previousFavorites);
            if (err?.data) toast(__(err?.data?.code), 'error');
        },
        onSettled: () => {
            // Always refetch after mutation
            queryClient.invalidateQueries({ queryKey: ['profiles', 'favorites'] });
        },
    });

    const removeFavorite = useMutation({
        mutationFn: async (channelId: string) => {
            setIsFavorite(false);
            const selectedProfile = userHelper?.getProfile();
            const favoritesChannelsService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
            await favoritesChannelsService.deleteFavoriteChannels(
                userHelper.getAPIKey(),
                selectedProfile?.profileGuid,
                [channelId],
                userHelper.getUserToken(),
                userHelper.getIdentityToken()
            );
            return channelId;
        },
        onMutate: async (channelId: string) => {
            await queryClient.cancelQueries({ queryKey: ['profiles', 'favorites'] });
            const previousFavorites = queryClient.getQueryData<string[]>(['profiles', 'favorites']);
            // Optimistically remove the favorite
            queryClient.setQueryData<string[]>(['profiles', 'favorites'], (old = []) =>
                old.filter((fav) => fav !== channelId)
            );
            return { previousFavorites };
        },
        onError: (err: any, channelId, context) => {
            // Rollback on error
            setIsFavorite(true);
            queryClient.setQueryData(['profiles', 'favorites'], context?.previousFavorites);
            if (err?.data) toast(__(err?.data?.code), 'error');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['profiles', 'favorites'] });
        },
    });

    return {
        favoriteChanels,
        addFavorite,
        removeFavorite,
        isFavorite,
        setIsFavorite,
    };
};
